import {
  type PrevisaoPedido,
  criarPedidoSchema,
  dividirEmFichas,
  formatarDuracao,
  preverItem,
  somarPrevisoes,
} from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Search, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  BarraTitulo,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Campo, Input, Select, Textarea } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Cabecalho, Carregando, Linha, Pilula, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { toast } from '@/components/ui/toast'
import { formatarData } from '@/lib/datas'
import { ROTULO_STATUS, TOM_STATUS, prazoVencido } from '@/lib/pedidos'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/pedidos/')({ component: Pedidos })

type Situacao = 'abertos' | 'concluidos' | 'cancelados' | 'todos'
const SITUACOES: [Situacao, string][] = [
  ['abertos', 'Em aberto'],
  ['concluidos', 'Concluídos'],
  ['cancelados', 'Cancelados'],
  ['todos', 'Todos'],
]

/** Busca só depois de uma pausa na digitação (não consulta a cada tecla). */
function useTermoAtrasado(valor: string, ms = 300) {
  const [termo, setTermo] = useState(valor.trim())
  useEffect(() => {
    const id = setTimeout(() => setTermo(valor.trim()), ms)
    return () => clearTimeout(id)
  }, [valor, ms])
  return termo
}

interface ItemForm {
  produtoId: string
  quantidade: number
}

function Pedidos() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [situacao, setSituacao] = useState<Situacao>('abertos')
  const [busca, setBusca] = useState('')
  const termo = useTermoAtrasado(busca)
  // O último lançado primeiro: nada novo some no fim da lista.
  const pedidos = useQuery(
    trpc.pedidos.listar.queryOptions({
      situacao,
      ordem: 'recentes',
      limite: 200,
      ...(termo ? { busca: termo } : {}),
    }),
  )
  const origens = useQuery(trpc.cadastros.origens.listar.queryOptions())
  const produtos = useQuery(trpc.cadastros.produtos.listar.queryOptions())
  const g = useGavetaCadastro<{ id: string }>()
  const [itens, setItens] = useState<ItemForm[]>([{ produtoId: '', quantidade: 0 }])

  const criar = useMutation(
    trpc.pedidos.criar.mutationOptions({
      onSuccess: async ({ id }) => {
        await qc.invalidateQueries({ queryKey: trpc.pedidos.listar.queryKey() })
        g.fechar()
        toast.sucesso('Pedido lançado e enviado para a fila do primeiro setor.')
        await navigate({ to: '/gestao/pedidos/$pedidoId', params: { pedidoId: id } })
      },
    }),
  )

  const abrir = () => {
    setItens([{ produtoId: '', quantidade: 0 }])
    g.abrirNovo()
  }

  const submit = aoSubmeter({
    schema: criarPedidoSchema,
    opcionais: ['referenciaExterna', 'prazo', 'observacao'],
    extra: { itens },
    setErros: g.setErros,
    onValido: (dados) => criar.mutateAsync(dados),
  })

  const produtoDe = (id: string) => produtos.data?.find((p) => p.id === id)
  const previa = (item: ItemForm) => {
    const produto = produtoDe(item.produtoId)
    if (!produto || !Number.isInteger(item.quantidade) || item.quantidade <= 0) return null
    const fichas = dividirEmFichas({
      quantidade: item.quantidade,
      tamanhoLote: produto.tamanhoLote,
    })
    // Mesma conta que o pedido grava em cada etapa (K31: o previsto já ao digitar).
    const previsao = preverItem({
      quantidade: item.quantidade,
      tamanhoLote: produto.tamanhoLote,
      etapas: produto.etapas.map((e) => ({
        setorId: e.setor.id,
        setor: e.setor.nome,
        segundosPorPeca: e.segundosPorPeca,
        tempoPresetSeg: e.tempoPresetSeg,
      })),
    })
    const fichasTexto =
      fichas.length === 1 ? '1 ficha' : `${fichas.length} fichas: ${resumirLotes(fichas)}`
    return {
      fichas,
      previsao,
      texto:
        previsao.totalSeg > 0
          ? `${fichasTexto} · previsto ${formatarDuracao(previsao.totalSeg)}`
          : fichasTexto,
    }
  }
  const previas = itens.map(previa)
  const totalFichas = previas.reduce((n, pv) => n + (pv?.fichas.length ?? 0), 0)
  const previsaoPedido = somarPrevisoes(previas.flatMap((pv) => (pv ? [pv.previsao] : [])))

  return (
    <>
      <BarraTitulo
        titulo="Pedidos"
        descricao="Lance o pedido como vendeu; o sistema divide em fichas e manda para o primeiro setor da sequência."
        onNovo={abrir}
        rotuloNovo="Novo pedido"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Situação" className="flex flex-wrap gap-1.5">
          {SITUACOES.map(([valor, rotulo]) => (
            <Button
              key={valor}
              size="sm"
              variant={situacao === valor ? 'primary' : 'secondary'}
              aria-pressed={situacao === valor}
              onClick={() => setSituacao(valor)}
            >
              {rotulo}
            </Button>
          ))}
        </div>
        <label className="relative ml-auto w-full max-w-xs">
          <span className="sr-only">Buscar pedido</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
            aria-hidden
          />
          <Input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nº, referência ou produto"
            className="pl-9"
          />
        </label>
      </div>
      <Tabela>
        <Cabecalho>
          <Th>Nº</Th>
          <Th>Origem</Th>
          <Th>Itens</Th>
          <Th>Avanço</Th>
          <Th>Prazo</Th>
          <Th>Status</Th>
        </Cabecalho>
        <tbody>
          {pedidos.data?.map((p) => {
            const fichas = p.itens.flatMap((i) => i.fichas)
            const concluidas = fichas.filter((f) => f.status === 'CONCLUIDA').length
            return (
              <Linha key={p.id}>
                <Td numerico className="font-semibold">
                  <Link
                    to="/gestao/pedidos/$pedidoId"
                    params={{ pedidoId: p.id }}
                    className="hover:text-brand"
                  >
                    #{p.numero}
                  </Link>
                </Td>
                <Td>{p.origem.nome}</Td>
                <Td>
                  {p.itens.map((i) => (
                    <span key={i.id} className="block">
                      {i.quantidade}× {i.produto.nome}
                    </span>
                  ))}
                </Td>
                <Td numerico title={`${concluidas} de ${fichas.length} fichas concluídas`}>
                  {p.progresso.percentual}%
                </Td>
                <Td
                  numerico
                  className={`${prazoVencido(p.prazo, p.status) ? 'font-semibold text-vermelho' : ''}`}
                  {...(prazoVencido(p.prazo, p.status) ? { title: 'Prazo vencido' } : {})}
                >
                  {formatarData(p.prazo)}
                  {prazoVencido(p.prazo, p.status) && ' · vencido'}
                </Td>
                <Td>
                  <Pilula tom={TOM_STATUS[p.status] ?? 'neutro'}>
                    {ROTULO_STATUS[p.status] ?? p.status}
                  </Pilula>
                </Td>
              </Linha>
            )
          })}
          {pedidos.isPending && <Carregando colunas={6} />}
          {pedidos.data?.length === 0 && (
            <Vazio colunas={6}>
              {termo
                ? `Nenhum pedido encontrado para "${termo}".`
                : situacao === 'abertos'
                  ? 'Nenhum pedido em aberto.'
                  : 'Nenhum pedido nesta situação.'}
            </Vazio>
          )}
        </tbody>
      </Tabela>

      <Gaveta
        aberta={g.aberta}
        largura="lg"
        titulo="Novo pedido"
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta form="form-pedido" salvando={criar.isPending} onCancelar={g.fechar} />
        }
      >
        <Formulario id="form-pedido" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Origem" erro={g.erros.origemId}>
              {(p) => (
                <Select {...p} name="origemId" defaultValue="" autoFocus>
                  <option value="">Escolha…</option>
                  {origens.data
                    ?.filter((o) => o.ativo)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.nome}
                      </option>
                    ))}
                </Select>
              )}
            </Campo>
            <Campo
              label="Prazo de entrega"
              erro={g.erros.prazo}
              dica="Opcional. Define a ordem da fila."
            >
              {(p) => <Input {...p} name="prazo" type="date" />}
            </Campo>
          </div>
          <Campo
            label="Referência externa"
            erro={g.erros.referenciaExterna}
            dica="Nº do pedido no ERP / marketplace, se houver."
          >
            {(p) => <Input {...p} name="referenciaExterna" />}
          </Campo>
          <Campo
            label="Observação"
            erro={g.erros.observacao}
            dica="Opcional. Aparece no detalhe do pedido."
          >
            {(p) => <Textarea {...p} name="observacao" rows={2} className="min-h-20" />}
          </Campo>

          <fieldset className="mt-2 flex flex-col gap-3">
            <legend className="mb-1 font-semibold">Itens</legend>
            {g.erros.itens && (
              <p role="alert" className="text-xs font-medium text-vermelho">
                {g.erros.itens}
              </p>
            )}
            {itens.map((item, i) => {
              const pv = previas[i]
              return (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_7rem_auto] items-start gap-2 rounded-xl bg-elevated p-3"
                >
                  <Campo label="Produto" erro={g.erros[`itens.${i}.produtoId`]}>
                    {(p) => (
                      <Select
                        {...p}
                        value={item.produtoId}
                        onChange={(ev) =>
                          setItens((is) =>
                            is.map((x, j) => (j === i ? { ...x, produtoId: ev.target.value } : x)),
                          )
                        }
                      >
                        <option value="">Escolha…</option>
                        {produtos.data
                          ?.filter((pr) => pr.ativo)
                          .map((pr) => (
                            <option key={pr.id} value={pr.id}>
                              {pr.nome} ({pr.codigo})
                            </option>
                          ))}
                      </Select>
                    )}
                  </Campo>
                  <Campo label="Quantidade" erro={g.erros[`itens.${i}.quantidade`]}>
                    {(p) => (
                      <Input
                        {...p}
                        type="number"
                        min={1}
                        value={item.quantidade || ''}
                        onChange={(ev) =>
                          setItens((is) =>
                            is.map((x, j) =>
                              j === i ? { ...x, quantidade: Number(ev.target.value) } : x,
                            ),
                          )
                        }
                      />
                    )}
                  </Campo>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Remover item"
                    className="mt-7"
                    disabled={itens.length === 1}
                    onClick={() => setItens((is) => is.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                  {pv && <p className="col-span-3 text-xs text-fg-muted">{pv.texto}</p>}
                </div>
              )
            })}
            <div className="flex items-center justify-between">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setItens((is) => [...is, { produtoId: '', quantidade: 0 }])}
              >
                <Plus className="size-4" aria-hidden /> Adicionar item
              </Button>
              {totalFichas > 0 && (
                <span className="text-sm text-fg-muted">
                  Serão geradas <strong className="text-fg">{totalFichas}</strong> fichas
                </span>
              )}
            </div>
            {previsaoPedido.totalSeg > 0 && <PrevisaoDoPedido previsao={previsaoPedido} />}
          </fieldset>
        </Formulario>
      </Gaveta>
    </>
  )
}

/** Tempo previsto do pedido por setor, para a PCP ver a carga antes de lançar (K31). */
function PrevisaoDoPedido({ previsao }: { previsao: PrevisaoPedido }) {
  return (
    <section aria-label="Tempo previsto do pedido" className="rounded-xl bg-elevated p-3 text-sm">
      <p className="flex items-baseline justify-between gap-3">
        <span className="font-semibold">Tempo previsto</span>
        <strong className="text-lg tabular">{formatarDuracao(previsao.totalSeg)}</strong>
      </p>
      <ul className="mt-1 flex flex-col gap-0.5 text-fg-muted">
        {previsao.porSetor.map((s) => (
          <li key={s.setorId} className="flex justify-between gap-3">
            <span>{s.setor}</span>
            <span className="tabular">{formatarDuracao(s.segundos)}</span>
          </li>
        ))}
      </ul>
      {previsao.etapasSemTempo > 0 && (
        <p className="mt-2 text-xs text-amarelo">
          {previsao.etapasSemTempo === 1
            ? '1 etapa sem tempo cadastrado não entra na conta.'
            : `${previsao.etapasSemTempo} etapas sem tempo cadastrado não entram na conta.`}{' '}
          Cadastre em Produtos.
        </p>
      )}
    </section>
  )
}

/** [80, 80, 80, 20] → "3 × 80 + 20" */
export function resumirLotes(fichas: number[]): string {
  const partes: string[] = []
  let atual: number | null = null
  let vezes = 0
  for (const q of [...fichas, Number.NaN]) {
    if (q === atual) {
      vezes++
      continue
    }
    if (atual != null) partes.push(vezes > 1 ? `${vezes} × ${atual}` : String(atual))
    atual = q
    vezes = 1
  }
  return partes.join(' + ')
}
